import React, { useEffect, useState } from 'react';
import { AssignmentLogEntry, Tool, Person, Warehouse, Users, ToolHistory } from '../../types';
import { UserIcon, ToolIcon, LocationMarkerIcon, CameraIcon, WarehouseIcon, ArrowRightIcon } from './Icons';
import { RESTHISTORY } from '../../constants';
import { wsurl } from '../../CommonFunctions';

interface HistoryViewProps {
  assignmentLogs: ToolHistory[];
  tools: Tool[];
  people: Users[];
  history: ToolHistory[];
  warehouses: Warehouse[];
  onReturnTools: (logEntryId: number) => void;
}

const HistoryView: React.FC<HistoryViewProps> = ({ assignmentLogs, tools, people, warehouses, onReturnTools }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPersonId, setFilterPersonId] = useState<number | null>(null);

  //const [history, setHistory] = useState<History[]>([]);

  const getToolName = (toolId: number) => tools.find(t => t.id === toolId)?.txt || 'Attrezzo Sconosciuto';
  const getPersonName = (personId: number) => people.find(p => p.id === personId)?.name || 'Persona Sconosciuta';
  
  const histassignimageurl = (histid:number) => {
    const imageUrl = wsurl + '/history/assign/image/' + histid;
    return imageUrl;
    
  };

  const histreleaseimageurl = (histid:number) => {
    const imageUrl = wsurl + '/history/release/image/' + histid;
    return imageUrl;
    
  };

  const toolimageurl = (toolid:number) => {
    const imageUrl = wsurl + '/tools/image/' + toolid;
    return imageUrl;
    
  };

  const getWarehouseForReturnedTool = (logEntry: AssignmentLogEntry): Warehouse | undefined => {
    // This is a simplification. We assume tools are returned to their original warehouse
    // or a default one if original is not easily determined without more complex state.
    // For now, let's find the first tool in the log and see its *original* warehouseId before assignment.
    // This logic would need to be robust in a real app, perhaps storing return warehouse in the log.
    if (logEntry.returnDate) {
        const firstToolId = logEntry.toolIds[0];
        const tool = tools.find(t => t.id === firstToolId);
        
        // If the tool is now in a warehouse, that's its current location.
        /*if (tool?.warehouseId) {
            return warehouses.find(w => w.id === tool.warehouseId);
        }*/
    }
    return undefined;
  };

  /*useEffect(()=>{
    const fetchhist = async()=>{
      const response = await RESTHISTORY();
      setHistory(response);
    };
    fetchhist();
  },[])*/

  const filteredLogs = assignmentLogs
    .filter(log => {
      const assigneeName = getPersonName(log.userid).toLowerCase();
      const toolNames = getToolName(log.toolid).toLowerCase();
      const destination = log.destination?.toLowerCase();
      const searchMatch = searchTerm === '' || 
                          assigneeName.includes(searchTerm.toLowerCase()) ||
                          toolNames.includes(searchTerm.toLowerCase()) ||
                          destination?.includes(searchTerm.toLowerCase());
      const personMatch = !filterPersonId || log.userid === filterPersonId;
      return searchMatch && personMatch;
    })
    .sort((a, b) => new Date(b.assigned).getTime() - new Date(a.assigned).getTime());

  if (assignmentLogs.length === 0) {
    return <p className="text-center text-slate-400 py-10">Nessuna registrazione storica presente.</p>;
  }

  return (
    <div className="h-full max-h-3/4 p-4 sm:p-6">
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <select
          value={filterPersonId || ''}
          onChange={(e) => setFilterPersonId(Number(e.target.value) || null)}
          className="p-3 rounded-lg bg-slate-800 text-slate-200 border border-slate-700 focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none min-w-[200px]"
          aria-label="Filtra per persona"
        >
          <option value="">Tutte le Persone</option>
          {people.map(p => <option key={p.id} value={p.id}>{p.name} {p.surname}</option>)}
        </select>
        <input
          type="text"
          placeholder="Cerca (persona, attrezzo, destinazione)..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-grow p-3 rounded-lg bg-slate-800 text-slate-200 border border-slate-700 focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none placeholder-slate-500"
        />
        
      </div>

      {filteredLogs.length === 0 && (
         <p className="text-center text-slate-400 py-10">Nessuna registrazione storica trovata per i filtri applicati.</p>
      )}

      <div className="space-y-6">
        {filteredLogs.map(log => {
          const assignee = people.find(p => p.id === log.userid);
          const assignedToolsInLog = tools.find(t => t.id === log.toolid);
          const returnedToWarehouse = warehouses.find(p => p.id === assignedToolsInLog?.warehouse);

          return (
            <div key={log.id} className="bg-slate-800 p-5 rounded-lg shadow-lg border border-slate-700">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3">
                <div>
                  <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                    log.released ? 'bg-emerald-600 text-emerald-100' : 'bg-amber-600 text-amber-100'
                  }`}>
                    {log.released ? 'RESTITUITO' : 'ASSEGNATO'}
                  </span>
                  <p className="text-sm text-slate-400 mt-1">
                    Data Assegnazione: {new Date(log.assigned).toLocaleString()}
                    {log.released && (
                        <span className="block sm:inline sm:ml-2">Data Restituzione: {new Date(log.released!).toLocaleString()}</span>
                    )}
                  </p>
                </div>
                {!log.released && false && (
                    <button 
                        onClick={() => onReturnTools(log.toolid)}
                        className="mt-2 sm:mt-0 px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-500 transition whitespace-nowrap"
                    >
                        Segna come Restituiti
                    </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                <div>
                  <p className="text-slate-400 text-sm mb-1 flex items-center"><UserIcon className="w-4 h-4 mr-2 text-sky-400" /> Assegnato a: 
                  <span className="px-3 text-slate-100 font-medium"> {assignee?.name || 'N/A'} </span></p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm mb-1 flex items-center"><LocationMarkerIcon className="w-4 h-4 mr-2 text-green-400" /> Destinazione:
                  <span className="px-3 text-slate-100 font-medium">{log.destination}</span></p>
                </div>
              </div>
              
              {returnedToWarehouse && log.released && (
                <div className="mb-3">
                    <p className="text-slate-400 text-sm mb-1 flex items-center"><WarehouseIcon className="w-4 h-4 mr-2 text-teal-400" /> Restituito a Magazzino:
                    <span className="px-3 text-slate-100 font-medium">{returnedToWarehouse.name}</span></p>
                </div>
              )}

              <div>
                <p className="text-slate-400 text-sm mb-1 flex items-center"><ToolIcon className="w-4 h-4 mr-2 text-sky-400" /> Attrezzi:</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  {assignedToolsInLog && (
                    <li key={assignedToolsInLog.id} className="text-slate-300 text-sm">{assignedToolsInLog.txt}
                       <img src={toolimageurl(assignedToolsInLog.id!)} onClick={()=>window.open(toolimageurl(assignedToolsInLog.id!), '_blank')} alt={assignedToolsInLog.txt} className="max-h-24 w-auto rounded-md border border-slate-600 shadow-md" />
                    </li>
                  )}
                </ul>
              </div>
              
              {log.assignnote && (
                <div className="mt-3">
                  <p className="text-slate-400 text-sm mb-1">Note assegnazione:</p>
                  <p className="text-slate-300 text-sm bg-slate-700 p-2 rounded">{log.assignnote}</p>
                </div>
              )}

              {log.assigned && (
                <div className="mt-4">
                  <p className="text-slate-400 text-sm mb-1 flex items-center"><CameraIcon className="w-4 h-4 mr-2 text-sky-400" /> Foto conferma assegnazione:</p>
                  <img src={histassignimageurl(log.id!)} onClick={()=>window.open(histassignimageurl(log.id!), '_blank')} alt="Conferma assegnazione" className="max-h-48 w-auto rounded-md border border-slate-600 shadow-md" />
                </div>
              )}
              {log.releasenote && (
                <div className="mt-3">
                  <p className="text-slate-400 text-sm mb-1">Note rilascio:</p>
                  <p className="text-slate-300 text-sm bg-slate-700 p-2 rounded">{log.releasenote}</p>
                </div>
              )}
              {log.released && (
                <div className="mt-4">
                  <p className="text-slate-400 text-sm mb-1 flex items-center"><CameraIcon className="w-4 h-4 mr-2 text-sky-400" /> Foto conferma restituzione:</p>
                  <img src={histreleaseimageurl(log.id!)} onClick={()=>window.open(histreleaseimageurl(log.id!), '_blank')} alt="Conferma restituzione" className="max-h-48 w-auto rounded-md border border-slate-600 shadow-md" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/*{log.assignimgsrc.startsWith('data:image') ? (
                    <img src={log.confirmationPhoto} alt="Conferma" className="max-h-48 w-auto rounded-md border border-slate-600 shadow-md" />
                  ) : (
                    <a href={log.confirmationPhoto} target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">{log.confirmationPhoto}</a>
                  )}*/

export default HistoryView;
